import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    MagnifyingGlassIcon,
    EyeIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    UserIcon,
    PhoneIcon,
    EnvelopeIcon,
    XMarkIcon,
    BoltIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline'
import { enquiryAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminEnquiryQuick = () => {
    return (
        <PermissionWrapper requiredPermission="enquiries">
            <AdminEnquiryQuickContent />
        </PermissionWrapper>
    )
}

const AdminEnquiryQuickContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        page: 1,
        limit: 10,
        sort: 'createdAt',
        startDate: '',
        endDate: ''
    })
    const [selectedEnquiry, setSelectedEnquiry] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-enquiry-quick', filters],
        queryFn: () => enquiryAPI.getEnquiries({ ...filters, source: 'enquiry' }),
        keepPreviousData: true,
    })

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status }) => enquiryAPI.updateEnquiryStatus(id, status),
        onSuccess: () => {
            toast.success('Enquiry status updated successfully!')
            queryClient.invalidateQueries(['admin-enquiry-quick'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update enquiry status')
        },
    })

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleViewEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry)
        setIsModalOpen(true)
    }

    const handleStatusUpdate = (enquiryId, status) => {
        updateStatusMutation.mutate({ id: enquiryId, status })
    }

    const handleExport = async () => {
        try {
            const params = { ...filters, source: 'enquiry' };
            delete params.page;
            delete params.limit;

            const response = await enquiryAPI.exportEnquiries(params);
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `quick_enquiries_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Enquiries exported successfully');
        } catch (error) {
            toast.error('Failed to export enquiries');
        }
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load quick enquiries" onRetry={refetch} />
            </div>
        )
    }

    const enquiries = data?.data?.enquiries || []
    const pagination = data?.data?.pagination || {}

    const getStatusColor = (status) => {
        switch (status) {
            case 'new': return 'bg-blue-100 text-blue-800'
            case 'contacted': return 'bg-yellow-100 text-yellow-800'
            case 'qualified': return 'bg-green-100 text-green-800'
            case 'converted': return 'bg-purple-100 text-purple-800'
            case 'rejected': return 'bg-red-100 text-red-800'
            case 'follow_up': return 'bg-orange-100 text-orange-800'
            default: return 'bg-gray-100 text-gray-800'
        }
    }

    return (
        <>
            <Helmet>
                <title>Quick Enquiries | Admin Panel</title>
            </Helmet>

            <div className="space-y-6 container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Quick Enquiries</h1>
                        <p className="text-gray-600">Manage quick enquiry requests from universities and courses</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                            {pagination.total_count || 0} Total
                        </div>
                        <button
                            onClick={handleExport}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow p-4 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="sm:col-span-2 lg:col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
                                    placeholder="Search enquiries..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="">All Status</option>
                                <option value="new">New</option>
                                <option value="contacted">Contacted</option>
                                <option value="qualified">Qualified</option>
                                <option value="converted">Converted</option>
                                <option value="rejected">Rejected</option>
                                <option value="follow_up">Follow Up</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                            <select
                                value={filters.sort}
                                onChange={(e) => handleFilterChange('sort', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="createdAt">Newest First</option>
                                <option value="-createdAt">Oldest First</option>
                                <option value="name">Name A-Z</option>
                                <option value="-name">Name Z-A</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                        </div>

                        <div className="flex items-end">
                            <button
                                onClick={() => setFilters({ search: '', status: '', page: 1, limit: 10, sort: 'createdAt', startDate: '', endDate: '' })}
                                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Enquiries Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Contact Info
                                            </th>
                                            <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                University/Course/Degree
                                            </th>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date
                                            </th>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {enquiries.map((enquiry) => (
                                            <motion.tr
                                                key={enquiry._id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="hover:bg-gray-50"
                                            >
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-10 w-10">
                                                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                                                <BoltIcon className="h-6 w-6 text-orange-600" />
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {enquiry.name}
                                                            </div>
                                                            <div className="text-sm text-gray-500 flex items-center">
                                                                <EnvelopeIcon className="h-4 w-4 mr-1" />
                                                                <span className="truncate max-w-[150px] sm:max-w-none">{enquiry.email}</span>
                                                            </div>
                                                            <div className="text-sm text-gray-500 flex items-center">
                                                                <PhoneIcon className="h-4 w-4 mr-1" />
                                                                {enquiry.phone}
                                                            </div>
                                                            <div className="text-sm text-gray-500 sm:hidden">
                                                                {enquiry.universityId?.name || enquiry.courseId?.title || enquiry.degreeName || 'N/A'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {enquiry.universityId?.name || enquiry.courseId?.title || enquiry.degreeName || 'N/A'}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {enquiry.degreeLevel && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 mr-2">
                                                                {enquiry.degreeLevel.charAt(0).toUpperCase() + enquiry.degreeLevel.slice(1)}
                                                            </span>
                                                        )}
                                                        {enquiry.city}, {enquiry.state}
                                                    </div>
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(enquiry.status)}`}>
                                                        {enquiry.status}
                                                    </span>
                                                </td>
                                                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {new Date(enquiry.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => handleViewEnquiry(enquiry)}
                                                            className="text-primary-600 hover:text-primary-900"
                                                        >
                                                            <EyeIcon className="h-5 w-5" />
                                                        </button>
                                                        <select
                                                            value={enquiry.status}
                                                            onChange={(e) => handleStatusUpdate(enquiry._id, e.target.value)}
                                                            className="text-xs border border-gray-300 rounded px-2 py-1"
                                                        >
                                                            <option value="new">New</option>
                                                            <option value="contacted">Contacted</option>
                                                            <option value="qualified">Qualified</option>
                                                            <option value="converted">Converted</option>
                                                            <option value="rejected">Rejected</option>
                                                            <option value="follow_up">Follow Up</option>
                                                        </select>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.total_pages > 1 && (
                                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.total_pages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> to{' '}
                                                <span className="font-medium">
                                                    {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)}
                                                </span> of{' '}
                                                <span className="font-medium">{pagination.total_count}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                                <button
                                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                                    disabled={pagination.current_page === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    Previous
                                                </button>
                                                {Array.from({ length: Math.min(pagination.total_pages, 5) }, (_, i) => {
                                                    const page = i + 1;
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === pagination.current_page
                                                                ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                })}
                                                <button
                                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                                    disabled={pagination.current_page === pagination.total_pages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    Next
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Enquiry Detail Modal */}
            <AnimatePresence>
                {isModalOpen && selectedEnquiry && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-6 py-4 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Quick Enquiry Details
                                    </h3>
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                            <div className="px-6 py-4 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Name</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEnquiry.name}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Email</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEnquiry.email}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEnquiry.phone}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">City</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedEnquiry.city}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">University/Course/Degree</label>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {selectedEnquiry.universityId?.name || selectedEnquiry.courseId?.title || selectedEnquiry.degreeName || 'N/A'}
                                        </p>
                                        {selectedEnquiry.degreeLevel && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 mt-1">
                                                {selectedEnquiry.degreeLevel.charAt(0).toUpperCase() + selectedEnquiry.degreeLevel.slice(1)} Level
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Status</label>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedEnquiry.status)}`}>
                                            {selectedEnquiry.status}
                                        </span>
                                    </div>
                                </div>
                                {selectedEnquiry.message && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Message</label>
                                        <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedEnquiry.message}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Submitted</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {new Date(selectedEnquiry.createdAt).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default AdminEnquiryQuick
